import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import DocumentDetailFooterForRegularUser from "components/document/read/common/footer/DocumentDetailFooterForRegularUser";
import ContractInfoOverviewSection from "components/document/read/analysis/ContractInfoDetail/ContractInfoOverviewSection";
import ContractInfoReviewResultSection from "components/document/read/analysis/ContractInfoDetail/ContractInfoReviewResultSection";
import AnalysisContinuousPreview from "components/document/read/analysis/ContractInfoDetail/AnalysisContinuousPreview";
import DocumentDetailPdfButtons from "components/document/read/common/header/DocumentDetailHeaderForRegularUser";
import useDocumentDetailQuery, {
  DocumentDetailResponseWithAny,
} from "features/document/hooks/queries/detail/useDocumentDetailQuery";
import DocumentViewer from "components/document/read/DocumentViewer";
import { concat } from "lodash-es";
import { pdf, View } from "@react-pdf/renderer";
import Divider from "components/foundation/Divider";
import CardLayout from "components/foundation/templates/CardLayout";
import Toast, { useToast } from "components/foundation/Toast";
import { ROUTER_PATH } from "config/path";
import { useDeleteDocumentMutation } from "features/document/hooks/mutations/useDeleteDocumentMutation";
import Modal from "components/foundation/Modal/Modal";
import Button from "components/foundation/Button";
import { TrashIcon } from "assets/icons";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import usePDF from "features/pdf/hooks/usePDF";

const Doc = React.memo(
  ({
    document,
    webPreview,
  }: {
    document: DocumentDetailResponseWithAny;
    webPreview?: boolean;
  }) => {
    return (
      <DocumentViewer
        formComponent={
          <View
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5mm",
            }}
          >
            <ContractInfoOverviewSection documentData={document.documentData} />
            {/* 1단계 결재 완료 상태일 때 결과 섹션 표시하지 않음 */}
            {document.approveStatus !== "REVIEW" &&
              document.approveStatus !== "REVIEW_IN_PROGRESS" && (
                <ContractInfoReviewResultSection
                  documentData={document.documentData}
                />
              )}
          </View>
        }
        companyName={document.companyName}
        documentClassName={document.documentClassName}
        documentApproveStatus={document.approveStatus}
        compactHeader
        flowFooter={webPreview}
        tags={concat(
          document?.tags.map((tag) => ({
            uuid: tag.tagUuid,
            name: tag.tagName,
          })),
          document?.freeTags,
        )}
        createdUserInfo={{
          name: document.createdUserDto?.name,
          signImageUrl: document.createdUserDto?.signImageUrl,
          date: document.documentCreatedAt || "",
        }}
        approvedUserInfo={{
          name: document.approvedUserDto?.name,
          signImageUrl: document.approvedUserDto?.signImageUrl,
          date: document.approveStatusUpdatedAt || "",
        }}
        attachmentList={document.attachments}
        approveComment={document.approveComment || ""}
      />
    );
  },
);
Doc.displayName = "Doc";

const AnalysisDetailPageForRegularMember = () => {
  const documentUuid = useParams().uuid;
  const safeDocumentUuid = documentUuid ?? "";

  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { mutate: deleteDocument } =
    useDeleteDocumentMutation(safeDocumentUuid);

  const handleDeleteDocument = () => {
    deleteDocument();
    navigate(ROUTER_PATH.document.analysis.dashboard);
  };

  const { data: document, refetch: refetchDocument } =
    useDocumentDetailQuery(documentUuid);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string>("");

  const { open: openToast, onOpenChange: onOpenChangeToast } = useToast();

  const { data: user } = useUserDetailQuery();
  const hasAuthorityToDelete = user?.adminType === "SUPERADMIN";

  const { uploadDocPdfAndGetS3Url } = usePDF<HTMLDivElement>();

  useEffect(() => {
    if (document) {
      setCurrentPdfUrl(document.documentFileUrl || "");
    }
  }, [document]);

  const handleUploadPdf = async (fileName: string) => {
    const { data: latestDocData } = await refetchDocument();

    if (!latestDocData) {
      throw new Error("최신 문서 데이터를 가져올 수 없습니다.");
    }

    const basePdfBlob = await pdf(
      <Doc document={latestDocData as DocumentDetailResponseWithAny} />,
    ).toBlob();

    const s3Url = await uploadDocPdfAndGetS3Url(basePdfBlob)(fileName);
    setCurrentPdfUrl(s3Url);
    return s3Url;
  };

  const prevBlobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!document) return;

    let cancelled = false;

    (async () => {
      const basePdfBlob = await pdf(
        <Doc
          document={document as DocumentDetailResponseWithAny}
          webPreview={true}
        />,
      ).toBlob();

      if (cancelled) return;

      const url = URL.createObjectURL(basePdfBlob);

      const prev = prevBlobUrlRef.current;
      prevBlobUrlRef.current = url;
      if (prev) setTimeout(() => URL.revokeObjectURL(prev), 1000);

      setPdfUrl(url);
    })();

    return () => {
      cancelled = true;
    };
  }, [document]);

  useEffect(() => {
    return () => {
      const last = prevBlobUrlRef.current;
      if (last) URL.revokeObjectURL(last);
    };
  }, []);

  return (
    <>
      <WrapStyle>
        {document && (
          <>
            <CardLayout.Container style={{ width: "100%" }}>
              <PdfLayoutStyle>
                <DocumentDetailPdfButtons
                  pdfUrl={currentPdfUrl ?? document.documentFileUrl}
                  pdfAvailable={
                    document.approveStatus === "APPROVE" ||
                    document.approveStatus === "REVIEW"
                  }
                  basePdfUrl={pdfUrl || undefined}
                  attachmentUrls={document.attachments.map(
                    (attachment) => attachment.fileUrl,
                  )}
                  className="pc-only"
                />
              </PdfLayoutStyle>

              <Divider className="pc-only" />

              {document ? (
                <PreviewScrollArea>
                  <PreviewScrollContent>
                    <AnalysisContinuousPreview
                      documentData={document.documentData}
                      documentClassName={document.documentClassName}
                      companyName={document.companyName}
                      documentApproveStatus={document.approveStatus}
                      tags={concat(
                        document?.tags.map((tag) => ({
                          uuid: tag.tagUuid,
                          name: tag.tagName,
                        })),
                        document?.freeTags,
                      )}
                      createdUserInfo={{
                        name: document.createdUserDto?.name,
                        signImageUrl: document.createdUserDto?.signImageUrl,
                        date: document.documentCreatedAt || "",
                      }}
                      approvedUserInfo={{
                        name: document.approvedUserDto?.name,
                        signImageUrl: document.approvedUserDto?.signImageUrl,
                        date: document.approveStatusUpdatedAt || "",
                      }}
                      attachmentList={document.attachments}
                      approveComment={document.approveComment || ""}
                      showReviewSection={
                        document.approveStatus !== "REVIEW" &&
                        document.approveStatus !== "REVIEW_IN_PROGRESS"
                      }
                    />
                  </PreviewScrollContent>
                </PreviewScrollArea>
              ) : null}
            </CardLayout.Container>

            <DocumentDetailFooterForRegularUser
              approvedUserName={document.approvedUserDto.name}
              approvedUserUuid={document.approvedUserDto.uuid}
              attachments={document.attachments}
              documentFileUrl={document.documentFileUrl}
              freeTags={document.freeTags}
              tags={document.tags}
              documentCategory={document.documentCategoryName}
              approveStatus={document.approveStatus || ""}
              uuid={document.uuid}
              permissionType={document.permissionType}
              onTagUpdateSuccess={() => onOpenChangeToast(true)}
              referenceUsers={document.referenceUsers}
              approveComment={document.approveComment || ""}
              author={document.createdUserDto?.uuid || ""}
              uploadDocPdfAndGetS3Url={handleUploadPdf}
              isAnalysis={true}
            />

            <DocumentDetailPdfButtons
              pdfUrl={currentPdfUrl || document.documentFileUrl}
              pdfAvailable={
                document.approveStatus === "APPROVE" ||
                document.approveStatus === "REVIEW"
              }
              basePdfUrl={pdfUrl || undefined}
              attachmentUrls={document.attachments.map(
                (attachment) => attachment.fileUrl,
              )}
              className="mobile-only"
            />
          </>
        )}
        <Modal.Root
          open={isDeleteModalOpen}
          onOpenChange={setIsDeleteModalOpen}
        >
          <Modal.Trigger asChild>
            {hasAuthorityToDelete && (
              <Button
                variant="tertiary-neg"
                size="large"
                fill
                className="mobile-only"
                leftIcon={<TrashIcon />}
                style={{ margin: "-3rem 0 1rem" }}
              >
                삭제
              </Button>
            )}
          </Modal.Trigger>
          <Modal.Layout>
            <Modal.Body
              title="문서를 삭제하시겠습니까?"
              onCancel={() => setIsDeleteModalOpen(false)}
              onConfirm={handleDeleteDocument}
            />
          </Modal.Layout>
        </Modal.Root>
      </WrapStyle>
      <Toast.Message open={openToast} onOpenChange={onOpenChangeToast}>
        태그가 수정되었습니다.
      </Toast.Message>
    </>
  );
};

export default AnalysisDetailPageForRegularMember;

const WrapStyle = styled.div`
  width: 100.5rem;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  gap: 2rem;

  & > * {
    width: 100%;
  }

  ${({ theme }) => theme.media.small} {
    .react-pdf__Document,
    .react-pdf__Page {
      width: 100% !important;
    }

    .react-pdf__Page {
      margin: 0 !important;
    }

    .react-pdf__Page__canvas {
      width: 100% !important;
      height: auto !important;
      display: block;
    }

    background-color: ${({ theme }) => theme.color.background.white};
    width: 100%;
  }
`;

const PdfLayoutStyle = styled.div`
  max-width: 100.5rem;
  width: 100%;
  margin: 0 auto;

  .react-pdf__Document {
    width: 100%;
  }
`;

const PreviewScrollArea = styled.div`
  width: 100%;
  overflow: visible;

  ${({ theme }) => theme.media.small} {
    overflow-x: hidden;
    overflow-y: visible;
  }
`;

const PreviewScrollContent = styled.div`
  width: 100%;
  margin: 0 auto;

  ${({ theme }) => theme.media.small} {
    width: 100%;
    min-width: 0;
    margin: 0;
  }
`;
